// Reddit OAuth Configuration
export const REDDIT_CONFIG = {
  CLIENT_ID: process.env.REACT_APP_REDDIT_CLIENT_ID || '',
  REDIRECT_URI: 'https://spriglaunch.com/auth/callback',
  RESPONSE_TYPE: 'code',
  DURATION: 'permanent',
  SCOPE: 'identity history read submit',
};

// Reddit OAuth URLs
export const REDDIT_URLS = {
  AUTHORIZE: 'https://www.reddit.com/api/v1/authorize',
  ACCESS_TOKEN: 'https://www.reddit.com/api/v1/access_token',
  VALIDATE_TOKEN: 'https://oauth.reddit.com/api/v1/me',
};

// Generate Reddit OAuth URL
export const getRedditAuthUrl = (): string => {
  // Generate and store a random state
  const state = Math.random().toString(36).substring(7);
  localStorage.setItem('reddit_auth_state', state);
  
  console.log('🔐 Generating Auth URL:', {
    clientId: REDDIT_CONFIG.CLIENT_ID,
    redirectUri: REDDIT_CONFIG.REDIRECT_URI,
    state,
    responseType: REDDIT_CONFIG.RESPONSE_TYPE
  });

  const params = new URLSearchParams({
    client_id: REDDIT_CONFIG.CLIENT_ID,
    response_type: REDDIT_CONFIG.RESPONSE_TYPE,
    state: state,
    redirect_uri: REDDIT_CONFIG.REDIRECT_URI,
    duration: REDDIT_CONFIG.DURATION,
    scope: REDDIT_CONFIG.SCOPE
  });

  return `${REDDIT_URLS.AUTHORIZE}?${params.toString()}`;
};

// Function to parse hash fragment from URL
export const parseHashParams = (hash: string): Record<string, string> => {
  const params: Record<string, string> = {};
  const hashWithoutPrefix = hash.startsWith('#') ? hash.substring(1) : hash;
  
  hashWithoutPrefix.split('&').forEach(pair => {
    const [key, value] = pair.split('=');
    if (key && value) {
      params[key] = decodeURIComponent(value);
    }
  });
  
  return params;
};

// New function to handle the implicit flow response
export const handleImplicitAuthResponse = (): { token?: string; error?: string } => {
  console.log('🔍 Checking hash fragment for token');
  const hash = window.location.hash;
  if (!hash) {
    console.log('No hash fragment found');
    return {};
  }

  const params = parseHashParams(hash);
  console.log('📍 Hash params:', { ...params, access_token: params.access_token ? '***' : undefined });

  const savedState = localStorage.getItem('reddit_auth_state');
  
  if (params.error) {
    console.error('❌ Auth Error:', params.error);
    return { error: params.error };
  }

  if (!params.state || params.state !== savedState) {
    console.error('❌ State mismatch:', { 
      received: params.state, 
      saved: savedState 
    });
    return { error: 'Invalid state parameter' };
  }

  if (params.access_token) {
    console.log('✅ Token received');
    localStorage.removeItem('reddit_auth_state');
    return { token: params.access_token };
  }

  return { error: 'No token found in response' };
};

// Handle Reddit OAuth token exchange
export const exchangeCodeForToken = async (code: string): Promise<string> => {
  console.group('🔄 Token Exchange');
  console.log('Starting token exchange with code:', code.substring(0, 5) + '...');
  
  try {
    console.log('Request Details:', {
      url: REDDIT_URLS.ACCESS_TOKEN,
      clientId: REDDIT_CONFIG.CLIENT_ID,
      redirectUri: REDDIT_CONFIG.REDIRECT_URI,
      method: 'POST'
    });

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: REDDIT_CONFIG.REDIRECT_URI,
    });

    console.log('Request Body:', body.toString());
    
    const tokenResponse = await fetch(REDDIT_URLS.ACCESS_TOKEN, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Basic ${btoa(`${REDDIT_CONFIG.CLIENT_ID}:`)}`,
      },
      body,
    });

    console.log('Response Status:', tokenResponse.status);
    console.log('Response Headers:', {
      contentType: tokenResponse.headers.get('content-type'),
      contentLength: tokenResponse.headers.get('content-length'),
    });
    
    if (!tokenResponse.ok) {
      const errorText = await tokenResponse.text();
      console.error('Token Exchange Error Response:', errorText);
      throw new Error(`Token exchange failed: ${errorText}`);
    }

    const data = await tokenResponse.json();
    console.log('Token Exchange Success:', {
      hasAccessToken: !!data.access_token,
      tokenType: data.token_type,
      scope: data.scope
    });
    
    console.groupEnd();
    return data.access_token;
  } catch (error) {
    console.error('Token Exchange Error:', error);
    console.groupEnd();
    throw error;
  }
};

// Validate Reddit token
export const validateToken = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch(REDDIT_URLS.VALIDATE_TOKEN, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.ok;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
}; 